import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./hueSpectrum.scss";
import { clamp } from "lodash";
import { calculateRgbFromHsv, IColorPickerHsv, IColorPickerRgb } from "../../colorPickerHelpers";
import { ColorHandle } from "./ColorHandle";

interface IHueSpectrumProps {
  hsv: IColorPickerHsv;
  dispatchUpdateHsv: (updatedHsv: IColorPickerHsv) => void;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

interface IHueSpectrumMoveRefState {
  prevHsv: IColorPickerHsv;
}

interface IHueSpectrumHandle {
  x: number;
  moving: boolean;
}

export const HUE_SPECTRUM_HEIGHT = 12;

export function HueSpectrum(props: IHueSpectrumProps) {
  const { hsv, dispatchUpdateHsv, onGestureStart, onGestureEnd } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const hueGradientRef = useRef<number[]>([]);
  const moveRefState = useRef<IHueSpectrumMoveRefState>({ prevHsv: null });
  const [handle, setHandle] = useState<IHueSpectrumHandle>({ x: 0, moving: false });

  const hueRgb: IColorPickerRgb = useMemo(
    () =>
      calculateRgbFromHsv({
        h: hsv.h,
        s: 100,
        v: 100,
      }),
    [hsv],
  );

  function clampHandleX(x: number) {
    const canvasBoundingRect = canvasRef.current?.getBoundingClientRect();
    if (x == null || !canvasBoundingRect) {
      return 0;
    }
    return clamp(x, 0, canvasBoundingRect.width);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) {
      return;
    }

    canvas.width = canvas.getBoundingClientRect().width;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const imageData = ctx.createImageData(canvas.width, canvas.height);
    const data = imageData.data;
    hueGradientRef.current = [];

    for (let y = 0; y <= canvas.height; y++) {
      for (let x = 0; x <= canvas.width; x++) {
        const h = (x / canvas.width) * 360;
        const i = (y * canvas.width + x) * 4;
        const pixelHsv: IColorPickerHsv = { h, s: 100, v: 100 };
        const pixelRgb = calculateRgbFromHsv(pixelHsv);
        data[i] = pixelRgb.r;
        data[i + 1] = pixelRgb.g;
        data[i + 2] = pixelRgb.b;
        data[i + 3] = 255;
        if (y === 0) {
          hueGradientRef.current.push(h);
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }, []);

  useEffect(() => {
    if (handle.moving) {
      return;
    }
    let updatedHandleX: number = null;
    let minDiff = Infinity;
    for (let x = 0; x < hueGradientRef.current.length && minDiff > 0; x++) {
      const diff = Math.abs(hsv.h - hueGradientRef.current[x]);
      if (diff < minDiff) {
        minDiff = diff;
        updatedHandleX = x;
      }
    }
    setHandle(prev => ({ ...prev, x: clampHandleX(updatedHandleX) }));
  }, [handle.moving, hsv.h]);

  const updateHsv = useCallback(
    (clientX: number, prevHsv: IColorPickerHsv) => {
      const canvasBoundingRect = canvasRef.current?.getBoundingClientRect();
      if (!prevHsv || !canvasBoundingRect) {
        return;
      }
      const x = clampHandleX(clientX - canvasBoundingRect.x);
      setHandle(prev => ({ ...prev, x }));
      const h = hueGradientRef.current[Math.round(x)];
      dispatchUpdateHsv({
        h,
        s: prevHsv.s,
        v: prevHsv.v,
      });
    },
    [dispatchUpdateHsv],
  );

  const handlePointerMove = useCallback((e: PointerEvent) => updateHsv(e.clientX, moveRefState.current.prevHsv), [updateHsv]);

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (canvasRef.current?.hasPointerCapture(e.pointerId)) {
        canvasRef.current.releasePointerCapture(e.pointerId);
      }
      handleRef.current?.releasePointerCapture(e.pointerId);
      moveRefState.current.prevHsv = null;
      setHandle(prev => ({ ...prev, moving: false }));
      onGestureEnd?.();
    },
    [onGestureEnd],
  );

  useEffect(() => {
    if (handle.moving) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handle.moving, handlePointerMove, handlePointerUp]);

  const onHandlePointerDown: React.PointerEventHandler<HTMLDivElement> = e => {
    if (e.button !== 0) {
      return;
    }
    if (handleRef.current) {
      onGestureStart?.();
      handleRef.current.setPointerCapture(e.pointerId);
      moveRefState.current.prevHsv = { ...hsv };
      setHandle(prev => ({ ...prev, moving: true }));
    }
  };

  const onCanvasPointerDown: React.PointerEventHandler<HTMLCanvasElement> = e => {
    if (e.button !== 0) {
      return;
    }
    onGestureStart?.();
    e.currentTarget.setPointerCapture(e.pointerId);
    moveRefState.current.prevHsv = { ...hsv };
    updateHsv(e.clientX, hsv);
    setHandle(prev => ({ ...prev, moving: true }));
  };

  return (
    <div className="hue-spectrum-container">
      <canvas
        ref={canvasRef}
        className="hue-spectrum"
        height={HUE_SPECTRUM_HEIGHT}
        onPointerDown={onCanvasPointerDown}
      />
      <ColorHandle
        ref={handleRef}
        x={handle.x}
        height={HUE_SPECTRUM_HEIGHT}
        rgb={hueRgb}
        moving={handle.moving}
        onPointerDown={onHandlePointerDown}
      />
    </div>
  );
}
